import React, { useState } from 'react'
import {
  Box, Chip, Collapse, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography
} from '@material-ui/core'
import { ExpandMore, ExpandLess } from '@material-ui/icons'

const round2 = v => Math.round(v * 100) / 100

const STATUS_COLORS = {
  costed: '#10b981',
  reviewed: '#10b981',
  materials_associated: '#f59e0b',
  service_assigned: '#f59e0b',
  categorized: '#ef4444',
  uncategorized: '#ef4444'
}

const AssemblySummary = ({ assemblies, url, projectId, summary }) => {
  const [expanded, setExpanded] = useState({})

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const totalCost = assemblies.reduce((sum, a) => sum + (a.costBreakdown?.totalCost || 0), 0)
  const totalMaterial = assemblies.reduce((sum, a) => sum + (a.costBreakdown?.materialCost || 0), 0)
  const totalLabor = assemblies.reduce((sum, a) => sum + (a.costBreakdown?.laborCost || 0), 0)
  const totalEquipment = assemblies.reduce((sum, a) => sum + (a.costBreakdown?.equipmentCost || 0), 0)

  return (
    <Box>
      {/* Summary Cards */}
      <Box display="flex" style={{ gap: 16 }} mb={2}>
        <SummaryCard label="Total Cost" value={`$${round2(totalCost).toLocaleString()}`} />
        <SummaryCard label="Material" value={`$${round2(totalMaterial).toLocaleString()}`} />
        <SummaryCard label="Labor" value={`$${round2(totalLabor).toLocaleString()}`} />
        <SummaryCard label="Equipment" value={`$${round2(totalEquipment).toLocaleString()}`} />
        <SummaryCard label="Items" value={assemblies.length} />
        <SummaryCard label="Costed" value={summary?.byStatus?.costed || 0} />
      </Box>

      {/* Output Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: 'var(--df-primary, #1f5e3a)' }}>
              <TableCell style={{ color: '#fbf8f1', fontWeight: 700, fontSize: 11, width: 30 }} />
              <TableCell style={{ color: '#fbf8f1', fontWeight: 700, fontSize: 11 }}>Item #</TableCell>
              <TableCell style={{ color: '#fbf8f1', fontWeight: 700, fontSize: 11 }}>Description</TableCell>
              <TableCell style={{ color: '#fbf8f1', fontWeight: 700, fontSize: 11 }}>Unit</TableCell>
              <TableCell style={{ color: '#fbf8f1', fontWeight: 700, fontSize: 11 }} align="right">Qty</TableCell>
              <TableCell style={{ color: '#fbf8f1', fontWeight: 700, fontSize: 11 }} align="right">Unit Price</TableCell>
              <TableCell style={{ color: '#fbf8f1', fontWeight: 700, fontSize: 11 }} align="right">Extension</TableCell>
              <TableCell style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assemblies.map(asm => {
              const bi = asm.bidItem || {}
              const totalCost = asm.costBreakdown?.totalCost || 0
              const qty = bi.quantity || 0
              const unitPrice = qty > 0 ? round2(totalCost / qty) : 0
              const isExpanded = expanded[asm._id]
              const statusColor = STATUS_COLORS[asm.assemblyStatus] || '#6b7280'

              return (
                <React.Fragment key={asm._id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleExpand(asm._id)}>
                        {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell style={{ fontSize: 12 }}>{bi.itemNo || '-'}</TableCell>
                    <TableCell style={{ fontSize: 12, maxWidth: 350 }}>
                      {bi.descriptionRaw || bi.descriptionNormalized || '-'}
                    </TableCell>
                    <TableCell style={{ fontSize: 12 }}>{bi.unitRaw || bi.unitNormalized || '-'}</TableCell>
                    <TableCell align="right" style={{ fontSize: 12 }}>{qty}</TableCell>
                    <TableCell align="right" style={{ fontSize: 12, fontWeight: 600 }}>
                      {totalCost > 0 ? `$${unitPrice.toLocaleString()}` : '-'}
                    </TableCell>
                    <TableCell align="right" style={{ fontSize: 12, fontWeight: 700 }}>
                      {totalCost > 0 ? `$${round2(totalCost).toLocaleString()}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={asm.assemblyStatus}
                        size="small"
                        style={{ backgroundColor: statusColor, color: '#fff', fontSize: 10, fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>

                  {/* Expanded cost breakdown */}
                  <TableRow>
                    <TableCell colSpan={8} style={{ padding: 0, border: 'none' }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box p={2} bgcolor="#fafafa" ml={4}>
                          <Box display="flex" style={{ gap: 24 }} mb={1}>
                            <CostLine label="Materials" value={asm.costBreakdown?.materialCost} />
                            <CostLine label="Labor" value={asm.costBreakdown?.laborCost} />
                            <CostLine label="Equipment" value={asm.costBreakdown?.equipmentCost} />
                            <CostLine label="Subcontract" value={asm.costBreakdown?.subcontractCost} />
                            <CostLine label="Disposal" value={asm.costBreakdown?.disposalCost} />
                          </Box>
                          {asm.serviceName && (
                            <Typography variant="caption" color="textSecondary">
                              Service: {asm.serviceName}
                            </Typography>
                          )}
                          {(asm.materials || []).length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" style={{ fontWeight: 600 }}>Materials:</Typography>
                              {asm.materials.map((m, i) => (
                                <Typography key={i} variant="caption" display="block" style={{ paddingLeft: 8 }}>
                                  {m.description} - {m.allocatedQty} {m.unit} @ ${m.unitPrice} = ${round2(m.totalMaterialCost)}
                                </Typography>
                              ))}
                            </Box>
                          )}
                          {(asm.recipeResult?.componentDetails || []).length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" style={{ fontWeight: 600 }}>Recipe Components:</Typography>
                              {asm.recipeResult.componentDetails.map((c, i) => (
                                <Typography key={i} variant="caption" display="block" style={{ paddingLeft: 8 }}>
                                  [{c.costType}] {c.description}: ${round2(c.extendedCost)}
                                  {c.hours ? ` (${round2(c.hours)}h)` : ''}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              )
            })}

            {/* Totals row */}
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell />
              <TableCell colSpan={5} style={{ fontWeight: 700, fontSize: 13 }}>TOTAL</TableCell>
              <TableCell align="right" style={{ fontWeight: 700, fontSize: 13 }}>
                ${round2(totalCost).toLocaleString()}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

const SummaryCard = ({ label, value }) => (
  <Box
    flex={1}
    p={2}
    bgcolor="#fff"
    border="1px solid #e0e0e0"
    borderRadius={8}
    textAlign="center"
  >
    <Typography variant="caption" color="textSecondary">{label}</Typography>
    <Typography variant="h6" style={{ fontWeight: 700 }}>{value}</Typography>
  </Box>
)

const CostLine = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="textSecondary">{label}</Typography>
    <Typography variant="body2" style={{ fontWeight: 600 }}>
      {value ? `$${round2(value).toLocaleString()}` : '-'}
    </Typography>
  </Box>
)

export default AssemblySummary
